/* Prenos stavki otpremnice u izlaznu fakturu — zajedničko za dinarsku i deviznu.
 *
 * Otpremnica nosi NETO cenu po stavci i valutu te cene (snimak u trenutku
 * otpreme). Faktura ima jednu valutu — SEF/UBL ne trpi mešanje valuta na jednom
 * dokumentu — pa se cena koja je u drugoj valuti preračunava po srednjem kursu
 * NBS-a. Bruto se ne prenosi: računa se iz neto i stope, da zbir stavki i PDV
 * na fakturi ne mogu da se raziđu.
 */

/* RSD u šifarniku valuta (model_gen grupa 18). */
export const RSD = 220;

const zaokruzi = (x) => Math.round((Number(x) || 0) * 100) / 100;

/* Srednji kurs za valutu, iz liste koju vraća GetInvoicesAndTypes.
   Lista nosi sve datume, pa se uzima poslednji unos za tu valutu.
   RSD nije u NBS listi — njegov kurs je 1. */
export const kursZaValutu = (valutaId, kursevi) => {
    if (!valutaId || Number(valutaId) === RSD) return 1;

    const redovi = (kursevi || []).filter(k => Number(k.id) === Number(valutaId) && Number(k.srednji) > 0);
    if (redovi.length === 0) return null;

    const poslednji = redovi.reduce((a, b) =>
        new Date(b.DateEntered) > new Date(a.DateEntered) ? b : a);
    return Number(poslednji.srednji);
};

/* Cena iz jedne valute u drugu, preko dinara (NBS kursevi su prema RSD).
   Vraća null ako kurs nedostaje — pozivalac tada javi, a ne upiše pogrešan broj. */
export const preracunajCenu = (cena, izValute, uValutu, kursevi) => {
    const iznos = Number(cena) || 0;
    if (!izValute || !uValutu || Number(izValute) === Number(uValutu)) return zaokruzi(iznos);

    const kursIz = kursZaValutu(izValute, kursevi);
    const kursU  = kursZaValutu(uValutu, kursevi);
    if (!kursIz || !kursU) return null;

    return zaokruzi(iznos * kursIz / kursU);
};

/* Valuta fakture: ako sve stavke imaju istu, faktura ide u njoj; inače ostaje
   ono što je forma već izabrala. */
export const jedinstvenaValuta = (stavke) => {
    const valute = [...new Set((stavke || []).map(s => Number(s.valuta)).filter(Boolean))];
    return valute.length === 1 ? valute[0] : null;
};

/* Stavke spremne za /invoice/InsertInvoiceItem.
   `sifra` je red iz šifarnika („PRODAJA ROBE") — iz njega idu stopa i tekst. */
export const spremiStavke = ({ stavke, sifraStavke, sifra, ciljValuta, kursevi }) => {
    const stopa = sifra?.PDVStopa ?? 20;

    return (stavke || []).map((st) => {
        const cena = preracunajCenu(st.cena, st.valuta, ciljValuta, kursevi);
        if (cena === null) return { greska: st, valuta: st.valuta };

        const kolicina = Number(st.kolicina) || 0;
        const neto = zaokruzi(kolicina * cena);
        const pdv = zaokruzi(neto * stopa / 100);

        return {
            sifraStavke,
            tekst: st.artikal,
            slobodanTekst: sifra?.TekstOslobodjenja || '',
            jedinicnaCena: cena,
            kolicina: st.kolicina,
            neto,
            bruto: zaokruzi(neto + pdv),
            PDVstopa: stopa,
            PDVIznos: pdv,
            id_magacin: st.id_magacin,
            artikal: st.id_artikal,
            jedinicaMere: st.jedinicaMere,
            rabatProc: null,
            rabat: null,
            id_otpremnica_stavka: st.id_otpremnica_stavka,
        };
    });
};

/* Zbir prenetih stavki u valuti fakture — ide u polje „Iznos" na zaglavlju.
   Računa se iz istih brojeva koji će biti upisani kao stavke, da se zbir i
   stavke ne mogu raziću. Stavka bez kursa se ne računa (ni ne prenosi se). */
export const ukupnoZaFakturu = ({ stavke, ciljValuta, kursevi }) => {
    const zbir = (stavke || []).reduce((suma, st) => {
        const cena = preracunajCenu(st.cena, st.valuta, ciljValuta, kursevi);
        if (cena === null) return suma;
        return suma + (Number(st.kolicina) || 0) * cena;
    }, 0);

    return zaokruzi(zbir);
};

/* Upis stavki — tek kad faktura dobije id; pre toga nemaju na šta da se zakače. */
export const upisiStavke = async ({ token, idFakture, stavke }) => {
    let upisano = 0;

    for (const formDataItems of stavke) {
        try {
            const odgovor = await fetch(process.env.REACT_APP_BACKEND_URL + '/invoice/InsertInvoiceItem', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
                body: JSON.stringify({ id: idFakture, formDataItems }),
            });
            const podaci = await odgovor.json();
            /* Procedura vraća ID na uspeh, a `message` kad odbije stavku
               (npr. nedostaje šifra ili iznos) — zato se gleda ID. */
            const red = Array.isArray(podaci) ? podaci[0] : podaci;
            if (odgovor.ok && red && (red.ID != null || red.id != null)) upisano += 1;
            else console.error('Stavka sa otpremnice odbijena:', red);
        } catch (e) {
            console.error('Stavka sa otpremnice nije upisana:', e);
        }
    }

    return upisano;
};
